/**
 * Loaders 10–20 — inline SVG with SMIL animations.
 * Loaders that use the shared gooey filter call useOrganicLoadersAssets()
 * to make sure the <defs> are in the DOM.
 */
import React from 'react';
import { DEFAULT_COLOR, useOrganicLoadersAssets } from './shared.jsx';

const A = { role: 'status', 'aria-label': 'Loading' };

export const Loader10 = ({ size = 200, color = DEFAULT_COLOR }) => (
  <svg width={size} height={size} viewBox="-70 -70 140 140" {...A}>
    <path fill={color}>
      <animate attributeName="d" dur="6s" repeatCount="indefinite"
        values="
          M 50 0 C 50 28, 28 50, 0 50 C -28 50, -50 28, -50 0 C -50 -28, -28 -50, 0 -50 C 28 -50, 50 -28, 50 0 Z;
          M 56 6 C 48 36, 22 52, -6 48 C -34 44, -54 22, -48 -8 C -42 -38, -18 -54, 10 -50 C 38 -46, 64 -24, 56 6 Z;
          M 48 -8 C 56 22, 36 54, 6 52 C -24 50, -54 32, -50 0 C -46 -32, -22 -56, 8 -52 C 38 -48, 40 -38, 48 -8 Z;
          M 50 0 C 50 28, 28 50, 0 50 C -28 50, -50 28, -50 0 C -50 -28, -28 -50, 0 -50 C 28 -50, 50 -28, 50 0 Z" />
    </path>
  </svg>
);

export const Loader11 = ({ size = 200, color = DEFAULT_COLOR }) => {
  useOrganicLoadersAssets();
  return (
    <svg width={size} height={size} viewBox="-80 -80 160 160" style={{ filter: 'url(#ol-goo)' }} {...A}>
      <g style={{ transformOrigin: '0 0', animation: 'ol-spin 2.4s linear infinite' }}>
        <circle r="20" cx="40"  cy="0"   fill={color}/>
        <circle r="20" cx="-20" cy="34"  fill={color}/>
        <circle r="20" cx="-20" cy="-34" fill={color}/>
      </g>
      <circle r="18" cx="0" cy="0" fill={color}/>
    </svg>
  );
};

export const Loader12 = ({ size = 200, color = DEFAULT_COLOR }) => {
  useOrganicLoadersAssets();
  return (
    <svg width={size} height={size} viewBox="-70 -70 140 140"
         style={{ animation: 'ol-spin 2.5s linear infinite' }} {...A}>
      <path fill="none" stroke={color} strokeWidth="10" strokeLinecap="round">
        <animate attributeName="d" dur="5s" repeatCount="indefinite"
          values="
            M 50 0 C 50 28, 28 50, 0 50 C -28 50, -50 28, -50 0 C -50 -28, -28 -50, 0 -50 C 28 -50, 50 -28, 50 0 Z;
            M 54 -6 C 50 28, 22 56, -8 50 C -38 44, -56 18, -48 -10 C -40 -38, -14 -54, 16 -48 C 46 -42, 58 -40, 54 -6 Z;
            M 50 0 C 50 28, 28 50, 0 50 C -28 50, -50 28, -50 0 C -50 -28, -28 -50, 0 -50 C 28 -50, 50 -28, 50 0 Z" />
        <animate attributeName="stroke-dasharray" dur="2.5s" repeatCount="indefinite"
          values="20 320; 200 140; 20 320" />
        <animate attributeName="stroke-dashoffset" dur="2.5s" repeatCount="indefinite"
          values="0; -160; -320" />
      </path>
    </svg>
  );
};

export const Loader13 = ({ size = 200, color = DEFAULT_COLOR }) => (
  <svg width={size} height={size * 0.5} viewBox="0 0 170 80" {...A}>
    <path d="M 5 40 C 25 5, 45 75, 65 40 S 105 5, 125 40 S 165 75, 165 40"
          fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeDasharray="20 16">
      <animate attributeName="stroke-dashoffset" dur="1.4s" repeatCount="indefinite" values="0; -72"/>
    </path>
  </svg>
);

export const Loader14 = ({ size = 200, color = DEFAULT_COLOR }) => {
  useOrganicLoadersAssets();
  return (
    <svg width={size * 0.6} height={size} viewBox="0 0 100 160"
         style={{ filter: 'url(#ol-goo-strong)' }} {...A}>
      <circle cx="50" cy="80" r="34" fill={color}/>
      <circle r="18" fill={color}>
        <animate attributeName="cx" dur="3.2s" repeatCount="indefinite" values="50;50;50"/>
        <animate attributeName="cy" dur="3.2s" repeatCount="indefinite" values="80;20;80"/>
      </circle>
      <circle r="14" fill={color}>
        <animate attributeName="cx" dur="3.8s" repeatCount="indefinite" values="50;50;50"/>
        <animate attributeName="cy" dur="3.8s" repeatCount="indefinite" values="80;140;80"/>
      </circle>
    </svg>
  );
};

export const Loader15 = ({ size = 200, color = DEFAULT_COLOR }) => {
  useOrganicLoadersAssets();
  return (
    <svg width={size} height={size} viewBox="-75 -75 150 150"
         style={{ animation: 'ol-spin 3.6s ease-in-out infinite' }} {...A}>
      <path fill={color}>
        <animate attributeName="d" dur="3s" repeatCount="indefinite"
          values="
            M 0 -55 C 30 -55, 55 -25, 48 0 C 55 25, 25 55, 0 50 C -25 55, -55 25, -48 0 C -55 -25, -30 -55, 0 -55 Z;
            M 0 -48 C 35 -55, 55 -30, 50 5 C 60 30, 20 60, -5 48 C -30 60, -60 20, -50 -5 C -55 -35, -30 -45, 0 -48 Z;
            M 0 -55 C 30 -55, 55 -25, 48 0 C 55 25, 25 55, 0 50 C -25 55, -55 25, -48 0 C -55 -25, -30 -55, 0 -55 Z" />
      </path>
    </svg>
  );
};

export const Loader16 = ({ size = 200, color = DEFAULT_COLOR }) => (
  <svg width={size} height={size} viewBox="-70 -70 140 140" {...A}>
    <circle fill="none" stroke={color} strokeWidth="8" cx="0" cy="0">
      <animate attributeName="r" dur="2.4s" repeatCount="indefinite"
        values="18; 56; 18" keySplines=".4 0 .2 1; .4 0 .2 1" calcMode="spline" keyTimes="0; .5; 1"/>
      <animate attributeName="stroke-opacity" dur="2.4s" repeatCount="indefinite" values="1; .15; 1"/>
    </circle>
    <circle fill={color} cx="0" cy="0">
      <animate attributeName="r" dur="2.4s" repeatCount="indefinite"
        values="10; 4; 10" keySplines=".4 0 .2 1; .4 0 .2 1" calcMode="spline" keyTimes="0; .5; 1"/>
    </circle>
  </svg>
);

export const Loader17 = ({ size = 200, color = DEFAULT_COLOR }) => {
  useOrganicLoadersAssets();
  return (
    <svg width={size} height={size} viewBox="-80 -80 160 160"
         style={{ filter: 'url(#ol-goo)' }} {...A}>
      <g style={{ animation: 'ol-spin 2.8s linear infinite', transformOrigin: '0 0' }}>
        <circle r="14" cx="44"  cy="0"   fill={color}/>
        <circle r="14" cx="14"  cy="42"  fill={color}/>
        <circle r="14" cx="-36" cy="26"  fill={color}/>
        <circle r="14" cx="-36" cy="-26" fill={color}/>
        <circle r="14" cx="14"  cy="-42" fill={color}/>
      </g>
    </svg>
  );
};

export const Loader18 = ({ size = 200, color = DEFAULT_COLOR }) => (
  <svg width={size * 0.6} height={size} viewBox="0 0 100 160" {...A}>
    <ellipse cx="50" cy="20" rx="22" ry="22" fill={color}>
      <animate attributeName="cy" dur="1.1s" repeatCount="indefinite"
        values="20;130;130;20;20" keyTimes="0;.45;.5;.95;1"
        keySplines=".4 0 .8 .4; 0 0 1 1; .2 .6 .4 1; 0 0 1 1" calcMode="spline"/>
      <animate attributeName="rx" dur="1.1s" repeatCount="indefinite"
        values="22;22;30;22;22" keyTimes="0;.4;.5;.6;1"/>
      <animate attributeName="ry" dur="1.1s" repeatCount="indefinite"
        values="22;22;14;22;22" keyTimes="0;.4;.5;.6;1"/>
    </ellipse>
  </svg>
);

export const Loader19 = ({ size = 200, color = DEFAULT_COLOR }) => (
  <svg width={size} height={size} viewBox="-75 -75 150 150" {...A}>
    <path fill={color} opacity=".35">
      <animate attributeName="d" dur="4.2s" repeatCount="indefinite"
        values="
          M 56 0 C 56 30, 30 56, 0 56 C -30 56, -56 30, -56 0 C -56 -30, -30 -56, 0 -56 C 30 -56, 56 -30, 56 0 Z;
          M 60 8 C 52 38, 24 60, -8 56 C -40 52, -60 24, -54 -8 C -48 -40, -20 -60, 12 -54 C 44 -48, 68 -22, 60 8 Z;
          M 56 0 C 56 30, 30 56, 0 56 C -30 56, -56 30, -56 0 C -56 -30, -30 -56, 0 -56 C 30 -56, 56 -30, 56 0 Z" />
    </path>
    <path fill={color}>
      <animate attributeName="d" dur="3s" repeatCount="indefinite"
        values="
          M 26 0 C 26 14, 14 26, 0 26 C -14 26, -26 14, -26 0 C -26 -14, -14 -26, 0 -26 C 14 -26, 26 -14, 26 0 Z;
          M 30 4 C 26 20, 12 30, -4 28 C -22 26, -30 12, -26 -4 C -22 -22, -8 -30, 8 -26 C 24 -22, 34 -10, 30 4 Z;
          M 26 0 C 26 14, 14 26, 0 26 C -14 26, -26 14, -26 0 C -26 -14, -14 -26, 0 -26 C 14 -26, 26 -14, 26 0 Z" />
    </path>
  </svg>
);

export const Loader20 = ({ size = 200, color = DEFAULT_COLOR }) => {
  useOrganicLoadersAssets();
  return (
    <svg width={size} height={size * 0.7} viewBox="-85 -20 170 120"
         style={{ filter: 'url(#ol-goo)' }} {...A}>
      <circle cx="0" cy="0" r="14" fill={color}/>
      <g>
        <animateTransform attributeName="transform" type="rotate"
          dur="1.6s" repeatCount="indefinite" calcMode="spline"
          values="-50;50;-50" keyTimes="0;.5;1"
          keySplines=".4 0 .6 1; .4 0 .6 1"/>
        <circle cx="0" cy="70" r="18" fill={color}/>
      </g>
      <g>
        <animateTransform attributeName="transform" type="rotate"
          dur="1.6s" repeatCount="indefinite" calcMode="spline"
          values="50;-50;50" keyTimes="0;.5;1"
          keySplines=".4 0 .6 1; .4 0 .6 1"/>
        <circle cx="0" cy="60" r="14" fill={color}/>
      </g>
    </svg>
  );
};
